// Heuristics for detecting test records in cached referrals / VitalRounds
// data. Used by the "Remove Test Data" checkbox on Quick Refer and
// Collaboration Analytics.
//
// The patterns and facility list below are intentionally hard-coded but
// exported so they're easy to move into an admin setting later (e.g. an
// "admin.test-data" page that edits a JSON config). Once that exists,
// callers can pass overrides into isTestRecord().

// Names that indicate the record is a test record. Applied to patient
// names AND provider names (any role — sending / creating / receiving)
// since VitalEngine staff often appear on any side as a test user.
//   - any token starting with "test" (TEST, TESTLEY, TESTING, ...)
//   - canonical placeholder names (john doe / jane doe)
const TEST_NAME_PATTERNS = [
  /\btest\w*\b/i,
  /\bjohn\s+doe\b/i,
  /\bjane\s+doe\b/i,
];

// Facilities that exist for VitalEngine internal testing. Match is
// case-insensitive substring on the facility name. Note that we *also*
// fire on any facility name containing a "test" token (see facilityIsTest
// below), so this list only needs the proper-noun test environments.
//
// "vitalengine" matches real test facilities named "VitalEngine" /
// "VE-ENGINEERING". It would ALSO match the Quick Refer placeholder
// "Non VitalEngine User Facility" (CMS-NPI physician not yet a signed-up
// VE user) — that exact phrase is allowlisted in FACILITY_NAME_EXCEPTIONS.
export const DEFAULT_TEST_FACILITY_NAME_FRAGMENTS = [
  'myheart',
  'vitalengine',
  've-engineering',
  've engineering',
];

/** Exact facility labels that contain a test fragment but are NOT test data. */
const FACILITY_NAME_EXCEPTIONS = [
  'non vitalengine user facility',
];

const f = (r, k) => (r?.data || r?.Data || {})[k];

function nameLooksLikeTest(name) {
  if (!name) return false;
  return TEST_NAME_PATTERNS.some(rx => rx.test(name));
}

function facilityIsTest(name, fragments) {
  if (!name) return false;
  const lower = String(name).trim().toLowerCase();
  if (FACILITY_NAME_EXCEPTIONS.includes(lower)) return false;
  if (fragments.some(frag => lower.includes(frag))) return true;
  // Any facility with a standalone "test" token in its name (e.g.
  // "453149 Test", "Heart Test Clinic") is also treated as a test
  // facility. \b ensures we don't match real words like "Testler".
  if (/\btest\b/i.test(name)) return true;
  return false;
}

/**
 * Single-string variant of the facility test-data check, exported so other
 * pages can apply the same heuristic to per-user rollup rows that only
 * carry a `facility_name` (logins / messages) rather than separate
 * sending/receiving fields. Drops the same set of names as isTestRecord
 * does for referrals: MyHeart/VitalEngine/VE-Engineering facility
 * fragments plus any standalone "test" token.
 */
export function isTestFacilityName(name, options = {}) {
  const facilityFragments = options.testFacilityFragments
    || DEFAULT_TEST_FACILITY_NAME_FRAGMENTS;
  return facilityIsTest(name, facilityFragments);
}

export function isTestRecord(record, options = {}) {
  const facilityFragments = options.testFacilityFragments
    || DEFAULT_TEST_FACILITY_NAME_FRAGMENTS;

  // 1) The upstream `test_referral` flag — when VitalEngine itself marks
  //    a referral as a test, trust it.
  const testFlag = f(record, 'test_referral');
  if (testFlag === true || testFlag === 1 || testFlag === '1' || testFlag === 'true') {
    return true;
  }

  // 2) Names — patient AND providers — with "test" tokens or John/Jane
  //    Doe placeholders. PHI may be masked on patients (e.g. "R** k****"),
  //    so the role-user names are often the strongest signal.
  //    See REFERRAL-FIELD-NAMING.md: sending_* / creating_* / receiving_*
  //    are the canonical roles. Check all three so a test name on any side
  //    flags the record.
  const patientName   = `${f(record, 'patient_first_name') || ''} ${f(record, 'patient_last_name') || ''}`.trim();
  const sendingUser   = f(record, 'sending_user_name')   || '';
  const creatingUser  = f(record, 'creating_user_name')  || '';
  const receivingUser = f(record, 'receiving_user_name') || '';
  if (nameLooksLikeTest(patientName))   return true;
  if (nameLooksLikeTest(sendingUser))   return true;
  if (nameLooksLikeTest(creatingUser))  return true;
  if (nameLooksLikeTest(receivingUser)) return true;

  // 3) Any role facility (sending / creating / receiving) on the test
  //    list or containing a "test" token. Any side counts — VitalEngine
  //    staff testing can appear as any role depending on the scenario.
  const sendingFac   = f(record, 'sending_facility');
  const creatingFac  = f(record, 'creating_facility');
  const receivingFac = f(record, 'receiving_facility');
  if (facilityIsTest(sendingFac,   facilityFragments)) return true;
  if (facilityIsTest(creatingFac,  facilityFragments)) return true;
  if (facilityIsTest(receivingFac, facilityFragments)) return true;

  return false;
}

export default isTestRecord;
